import { useEffect, useRef } from "react";
import { useImmer } from "use-immer";
import { toast } from "react-toastify";

import { WORKFLOW_IDS } from "@intelligems/ig-plugin/utils/workflow-ids";
import type { AgenticLogicSurface } from "@intelligems/ig-types/dtos/agentic-logic/surface";
import type { AiNodeContext } from "@intelligems/ig-types/schemas/ai/context-schemas";
import type { StartWorkflowResponseBodyDto } from "@intelligems/ig-types/dtos/ai/workflows/start-workflow";

import { agenticLogicRuleIdeasContextKey } from "@src/store/slices/appSettings";

export interface AgenticLogicRuleIdeasWorkflowVariables {
  ruleIdeas: {
    ideas: string[];
    placeholder: string;
  };
}

/** The ideas and the composer placeholder one workflow run generates. */
export interface AgenticLogicRuleIdeaSet {
  ideas: string[];
  placeholder: string;
}

const RULE_IDEAS_ERROR = "Atlas could not generate new rule ideas. Try again.";

// The prompt and the schema's `maxLength` ask for 100 characters, but the
// structured output runs non-strict and nothing enforces it: the model lands a
// few characters over. A suggestion row wraps, so only a runaway idea is
// unusable — rejecting a 102-character one threw the whole set away.
const RULE_IDEA_MAX_LENGTH = 160;

// The schema asks for 60 characters so the example fits on one line of the
// composer; the same non-strict slack applies, so only a runaway is refused.
const RULE_PLACEHOLDER_MAX_LENGTH = 90;

interface RunRuleIdeasWorkflowParams {
  workflowId: string;
  version: string;
  initialContext: Partial<AiNodeContext>;
}

type RunRuleIdeasWorkflow = (
  params: RunRuleIdeasWorkflowParams
) => Promise<StartWorkflowResponseBodyDto<AgenticLogicRuleIdeasWorkflowVariables> | null>;

interface AgenticLogicRuleIdeasPersistence {
  initialIdeas: readonly string[];
  /** Undefined until generated — and for sets persisted before it existed. */
  initialPlaceholder: string | undefined;
  onIdeasChange: (ideaSet: AgenticLogicRuleIdeaSet) => void;
  /**
   * The surface the ideas must be written for, when the creation view was
   * entered from a placement that already knows where it renders. Undefined on
   * the rules list, which is bound to no placement and where every surface is
   * still open.
   *
   * Part of the request AND of what makes a set stale: switching entries is a
   * different context, so the ideas are generated again rather than reused
   * from the previous one.
   */
  placementSurface?: AgenticLogicSurface;
}

export const useAgenticLogicRuleIdeas = (
  runWorkflow: RunRuleIdeasWorkflow,
  {
    initialIdeas,
    initialPlaceholder,
    onIdeasChange,
    placementSurface,
  }: AgenticLogicRuleIdeasPersistence
) => {
  const [ideaSet, setIdeaSet] = useImmer<AgenticLogicRuleIdeaSet | null>(() =>
    validRuleIdeaSet(initialIdeas, initialPlaceholder)
  );
  // Which context the ideas on screen were settled for. A context is settled
  // once — by adopting a usable persisted set or by one workflow run — so
  // re-entering the same placement costs nothing and entering a different one
  // never shows the previous placement's ideas.
  const settledContext = useRef<string | null>(null);
  const mounted = useRef(false);

  useEffect(() => {
    mounted.current = true;
    const context = agenticLogicRuleIdeasContextKey(placementSurface);
    if (settledContext.current !== context) {
      settledContext.current = context;
      const persistedSet = validRuleIdeaSet(initialIdeas, initialPlaceholder);
      if (persistedSet) {
        setIdeaSet(persistedSet);
      } else {
        // What this org has already been shown IN THIS CONTEXT, valid as a
        // whole set or not: a persisted set that failed validation still tells
        // the model which territory it covered, so the regeneration does not
        // hand back the same three ideas in a well-formed wrapper.
        void generateRuleIdeas(
          runWorkflow,
          nonEmptyStrings(initialIdeas),
          placementSurface
        ).then((nextSet) => {
          if (mounted.current && nextSet) {
            setIdeaSet(nextSet);
            onIdeasChange(nextSet);
          }
        });
      }
    }

    return () => {
      mounted.current = false;
    };
  }, [
    initialIdeas,
    initialPlaceholder,
    onIdeasChange,
    placementSurface,
    runWorkflow,
    setIdeaSet,
  ]);

  return {
    ideas: ideaSet?.ideas ?? [],
    placeholder: ideaSet?.placeholder ?? null,
  };
};

const generateRuleIdeas = async (
  runWorkflow: RunRuleIdeasWorkflow,
  ideasToAvoid: string[],
  placementSurface?: AgenticLogicSurface
) => {
  const response = await runWorkflow({
    workflowId: WORKFLOW_IDS.AGENTIC_LOGIC_RULE_IDEAS,
    version: "1.0.0",
    initialContext: {
      // `placementSurface` is omitted rather than sent as null when the entry
      // is bound to no placement: the prompt branches on it with mustache's
      // section idiom, and only an absent (or falsy) variable takes the "every
      // surface is open" branch.
      variables: placementSurface
        ? { ideasToAvoid, placementSurface }
        : { ideasToAvoid },
    },
  });
  const nextSet = validRuleIdeaSet(
    response?.variables?.ruleIdeas?.ideas,
    response?.variables?.ruleIdeas?.placeholder
  );
  if (!nextSet) {
    toast.error(RULE_IDEAS_ERROR);
    return null;
  }

  return nextSet;
};

const validRuleIdeaSet = (
  candidateIdeas: unknown,
  candidatePlaceholder: unknown
): AgenticLogicRuleIdeaSet | null => {
  const ideas = validRuleIdeas(candidateIdeas);
  const placeholder = validRulePlaceholder(candidatePlaceholder);
  return ideas && placeholder ? { ideas, placeholder } : null;
};

/**
 * The composer shows the example after its own "e.g.", so a prefix or a
 * closing period the model adds anyway is dropped rather than doubled.
 */
const validRulePlaceholder = (candidate: unknown) => {
  if (typeof candidate !== "string") {
    return null;
  }
  const placeholder = candidate
    .trim()
    .replace(/^e\.g\.,?\s*/i, "")
    .replace(/\.$/, "")
    .trim();
  return placeholder.length > 0 &&
    placeholder.length <= RULE_PLACEHOLDER_MAX_LENGTH
    ? placeholder
    : null;
};

const validRuleIdeas = (candidateIdeas: unknown) => {
  if (
    !Array.isArray(candidateIdeas) ||
    candidateIdeas.length !== 3 ||
    !candidateIdeas.every((idea): idea is string => typeof idea === "string")
  ) {
    return null;
  }

  const normalizedIdeas = candidateIdeas.map((idea) => idea.trim());
  if (
    normalizedIdeas.some(
      (idea) => idea.length === 0 || idea.length > RULE_IDEA_MAX_LENGTH
    ) ||
    new Set(normalizedIdeas).size !== normalizedIdeas.length
  ) {
    return null;
  }

  return normalizedIdeas;
};

/** The readable ideas in a persisted set, valid as a whole set or not. */
const nonEmptyStrings = (candidateIdeas: readonly unknown[]): string[] =>
  candidateIdeas
    .filter((idea): idea is string => typeof idea === "string")
    .map((idea) => idea.trim())
    .filter((idea) => idea.length > 0);
